import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AdminPermissionMap, AdminRole } from "../constants/adminPermissions";

interface AdminUser {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: AdminRole;
    permissions: AdminPermissionMap;
    preferences: {
        theme: "light" | "dark";
        notifications: {
            email: boolean;
            dashboard: boolean;
        };
    };
    twoFactor?: {
        enabled: boolean;
        enabledAt?: string | null;
        recoveryCodesRemaining: number;
    };
    twoFactorRequired?: boolean;
}

interface AdminAuthState {
    admin: AdminUser | null;
    token: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
}

interface AdminAuthActions {
    login: (email: string, password: string) => Promise<boolean>;
    logout: () => void;
    /** Adopt a session minted elsewhere — the login page issues its tokens over GraphQL. */
    setSession: (session: { admin: AdminUser; token: string; refreshToken: string }) => void;
    refreshAdminToken: () => Promise<boolean>;
    updatePreferences: (preferences: Partial<AdminUser["preferences"]>) => Promise<boolean>;
    clearError: () => void;
}

type AdminStore = AdminAuthState & AdminAuthActions;

export const useAdminStore = create<AdminStore>()(
    persist(
        (set, get) => ({
            // State
            admin: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,

            // Actions
            login: async (email: string, password: string) => {
                set({ isLoading: true, error: null });

                try {
                    const response = await fetch("/api/admin/login", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ email, password }),
                    });

                    const data = await response.json();

                    if (!response.ok) {
                        throw new Error(data.error || "Login failed");
                    }

                    set({
                        admin: data.admin,
                        token: data.token,
                        refreshToken: data.refreshToken,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null,
                    });

                    return true;
                } catch (error: any) {
                    set({
                        error: error.message,
                        isLoading: false,
                        isAuthenticated: false,
                    });
                    return false;
                }
            },

            logout: () => {
                set({
                    admin: null,
                    token: null,
                    refreshToken: null,
                    isAuthenticated: false,
                    error: null,
                });
            },

            setSession: ({ admin, token, refreshToken }) => {
                set({ admin, token, refreshToken, isAuthenticated: true, error: null });
            },

            refreshAdminToken: async () => {
                // localStorage is the fallback, not the afterthought: sessions
                // created before `setSession` existed, and any tab that reloaded
                // before the store rehydrated, only have it there.
                const refreshToken = get().refreshToken || localStorage.getItem("adminRefreshToken");
                if (!refreshToken) return false;

                try {
                    const response = await fetch("/api/admin/refresh", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ refreshToken }),
                    });

                    const data = await response.json();

                    if (!response.ok) {
                        throw new Error("Token refresh failed");
                    }

                    set({
                        token: data.token,
                        refreshToken,
                        isAuthenticated: true,
                    });

                    // Update localStorage as well
                    if (data.token) {
                        localStorage.setItem("adminToken", data.token);
                    }

                    return true;
                } catch (error) {
                    set({
                        isAuthenticated: false,
                        token: null,
                        refreshToken: null,
                    });
                    // Clear localStorage on refresh failure
                    localStorage.removeItem("adminToken");
                    localStorage.removeItem("adminRefreshToken");
                    localStorage.removeItem("adminUser");
                    return false;
                }
            },

            updatePreferences: async (preferences) => {
                const { token } = get();
                if (!token) return false;

                try {
                    const response = await fetch("/api/admin/preferences", {
                        method: "PUT",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(preferences),
                    });

                    if (!response.ok) {
                        throw new Error("Failed to update preferences");
                    }

                    const data = await response.json();
                    set((state) => ({
                        admin: state.admin ? { ...state.admin, preferences: { ...state.admin.preferences, ...data.preferences } } : null,
                    }));

                    return true;
                } catch (error) {
                    set({ error: "Failed to update preferences" });
                    return false;
                }
            },

            clearError: () => set({ error: null }),
        }),
        {
            name: "admin-storage",
            partialize: (state) => ({
                admin: state.admin,
                token: state.token,
                refreshToken: state.refreshToken,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
);

// Helper function to make authenticated admin API calls
export const adminApiCall = async (endpoint: string, options: RequestInit = {}) => {
    const { token } = useAdminStore.getState();
    
    if (!token) {
        throw new Error("No admin token available");
    }

    const response = await fetch(endpoint, {
        ...options,
        headers: {
            ...options.headers,
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        if (response.status === 401) {
            // Token expired, try to refresh
            const refreshed = await useAdminStore.getState().refreshAdminToken();
            if (refreshed) {
                // Retry the request with new token
                const newToken = useAdminStore.getState().token;
                return fetch(endpoint, {
                    ...options,
                    headers: {
                        ...options.headers,
                        Authorization: `Bearer ${newToken}`,
                        "Content-Type": "application/json",
                    },
                });
            } else {
                // Refresh failed, redirect to login
                useAdminStore.getState().logout();
                window.location.href = "/hq/login";
            }
        }
        throw new Error(`API call failed: ${response.statusText}`);
    }

    return response;
};
